import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import "./index.scss";

import {
    CALENDAR_COLOR_GROUPS,
    resolveCalendarColor,
} from "../../../constants/calendarColors";
import useFocusTrap from "../../../hooks/useFocusTrap";

const RECENT_STORAGE_KEY = "muzone.calendar.recentColors";
const MAX_RECENTS = 6;
const DEFAULT_CUSTOM_COLOR = "#4c6fff";

const PRESET_GROUP_TITLE_KEYS = Object.freeze({
    "Workspace palette": "colorTokenPicker.groups.workspacePalette",
    "Vibrant picks": "colorTokenPicker.groups.vibrantPicks",
});

const PRESET_COLOR_LABEL_KEYS = Object.freeze({
    Berry: "colorTokenPicker.colors.berry",
    Ocean: "colorTokenPicker.colors.ocean",
    Lime: "colorTokenPicker.colors.lime",
    Amber: "colorTokenPicker.colors.amber",
    Violet: "colorTokenPicker.colors.violet",
    Cobalt: "colorTokenPicker.colors.cobalt",
    Sunset: "colorTokenPicker.colors.sunset",
    Lagoon: "colorTokenPicker.colors.lagoon",
    Orchid: "colorTokenPicker.colors.orchid",
    Midnight: "colorTokenPicker.colors.midnight",
    Citrine: "colorTokenPicker.colors.citrine",
    Rose: "colorTokenPicker.colors.rose",
});

const translatePresetGroupTitle = (title, t) => {
    const key = PRESET_GROUP_TITLE_KEYS[title];
    return key ? t(key, title) : title;
};

const translatePresetColorLabel = (label, t) => {
    const key = PRESET_COLOR_LABEL_KEYS[label];
    return key ? t(key, label) : label;
};

const normalizeCandidate = (candidate) => {
    if (!candidate || typeof candidate !== "string") {
        return "";
    }
    return candidate.trim();
};

const ensureHex = (candidate) => {
    const trimmed = normalizeCandidate(candidate).toLowerCase();
    if (!trimmed) {
        return null;
    }
    const hexPattern = /^#?[0-9a-f]{3}([0-9a-f]{3})?$/;
    if (!hexPattern.test(trimmed)) {
        return null;
    }
    if (trimmed.startsWith("#")) {
        return trimmed;
    }
    return `#${trimmed}`;
};

const buildPresetIndex = (groups) => {
    const map = new Map();
    groups.forEach((group) => {
        group.items.forEach((item) => {
            map.set(item.value, item);
        });
    });
    return map;
};

const loadRecentColors = () => {
    if (typeof window === "undefined") {
        return [];
    }
    try {
        const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((item) => typeof item === "string").slice(0, MAX_RECENTS);
    } catch {
        return [];
    }
};

const persistRecentColors = (colors) => {
    if (typeof window === "undefined") {
        return;
    }
    try {
        window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(colors.slice(0, MAX_RECENTS)));
    } catch {
        // Ignore write errors (e.g. private browsing)
    }
};

const resolvePreviewColor = (value, presetIndex) => {
    const normalized = normalizeCandidate(value);
    if (!normalized) {
        return null;
    }
    const preset = presetIndex.get(normalized);
    if (preset) {
        return preset.swatch;
    }
    return resolveCalendarColor(normalized) || null;
};

const resolveDisplayText = (value, presetIndex, emptyLabel, t) => {
    const normalized = normalizeCandidate(value);
    if (!normalized) {
        return emptyLabel;
    }
    const preset = presetIndex.get(normalized);
    if (preset) {
        return translatePresetColorLabel(preset.label, t);
    }
    return normalized;
};

const addToRecents = (color, currentRecents) => {
    const normalized = normalizeCandidate(color);
    if (!normalized) {
        return currentRecents;
    }
    const filtered = currentRecents.filter((item) => item !== normalized);
    const next = [normalized, ...filtered];
    return next.slice(0, MAX_RECENTS);
};

const ColorTokenPicker = ({
    id,
    value,
    onChange,
    disabled = false,
    presetGroups = CALENDAR_COLOR_GROUPS,
    ariaLabelledby,
    ariaDescribedby,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [recentColors, setRecentColors] = useState(() => loadRecentColors());
    const [customInput, setCustomInput] = useState("");
    const [customError, setCustomError] = useState("");
    const triggerRef = useRef(null);
    const dialogRef = useRef(null);

    const presetIndex = useMemo(() => buildPresetIndex(presetGroups), [presetGroups]);
    const previewColor = useMemo(() => resolvePreviewColor(value, presetIndex), [value, presetIndex]);
    const emptyColorLabel = t("colorTokenPicker.empty", "Choose a color");
    const displayValue = useMemo(
        () => resolveDisplayText(value, presetIndex, emptyColorLabel, t),
        [emptyColorLabel, presetIndex, t, value],
    );

    useFocusTrap({
        active: isOpen,
        containerRef: dialogRef,
    });

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            if (!dialogRef.current || !triggerRef.current) {
                return;
            }
            const withinDialog = dialogRef.current.contains(event.target);
            const withinTrigger = triggerRef.current.contains(event.target);
            if (!withinDialog && !withinTrigger) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setCustomInput(normalizeCandidate(value));
            setCustomError("");
        }
    }, [isOpen, value]);

    useEffect(() => {
        persistRecentColors(recentColors);
    }, [recentColors]);

    const handleToggle = useCallback(() => {
        if (disabled) {
            return;
        }
        setIsOpen((prev) => !prev);
    }, [disabled]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleSelectColor = useCallback((nextValue) => {
        const normalized = normalizeCandidate(nextValue);
        if (!normalized) {
            return;
        }
        onChange?.(normalized);
        setRecentColors((prev) => addToRecents(normalized, prev));
        setIsOpen(false);
    }, [onChange]);

    const handleCustomChange = useCallback((event) => {
        setCustomInput(event.target.value);
        if (customError) {
            setCustomError("");
        }
    }, [customError]);

    const handleCustomSubmit = useCallback((event) => {
        event.preventDefault();
        const inputValue = normalizeCandidate(customInput);
        if (!inputValue) {
            setCustomError(t("colorTokenPicker.errors.empty", "Enter a token or hex value."));
            return;
        }
        if (presetIndex.has(inputValue)) {
            handleSelectColor(inputValue);
            return;
        }
        const hex = ensureHex(inputValue);
        if (!hex) {
            setCustomError(t("colorTokenPicker.errors.invalid", "Use a token (e.g. berry) or a hex color like #3366ff."));
            return;
        }
        handleSelectColor(hex);
    }, [customInput, handleSelectColor, presetIndex, t]);

    const handleClearSelection = useCallback(() => {
        onChange?.("");
        setIsOpen(false);
    }, [onChange]);

    return (
        <div className="color-token-picker">
            <button
                type="button"
                id={id}
                ref={triggerRef}
                className={`color-token-picker__control${disabled ? " color-token-picker__control--disabled" : ""}`}
                onClick={handleToggle}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedby}
                disabled={disabled}
            >
                <span
                    className={`color-token-picker__preview${previewColor ? "" : " color-token-picker__preview--empty"}`}
                    style={previewColor ? { backgroundColor: previewColor } : undefined}
                    aria-hidden="true"
                />
                <span className="color-token-picker__label">{displayValue}</span>
                <span className="color-token-picker__chevron" aria-hidden="true">▾</span>
            </button>
            {isOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("colorTokenPicker.dialogLabel", "Pick a color")}
                    className="color-token-picker__modal"
                    ref={dialogRef}
                    tabIndex={-1}
                >
                    <div className="color-token-picker__modal-header">
                        <div>
                            <h3>{t("colorTokenPicker.title", "Pick a color")}</h3>
                            <p>{t("colorTokenPicker.description", "Select a preset or create your own hue. Recent picks stay handy.")}</p>
                        </div>
                        <button
                            type="button"
                            className="color-token-picker__close"
                            onClick={handleClose}
                            aria-label={t("colorTokenPicker.close", "Close color picker")}
                        >
                            ×
                        </button>
                    </div>
                    <div className="color-token-picker__section">
                        <h4>{t("colorTokenPicker.presets", "Presets")}</h4>
                        <div className="color-token-picker__grid">
                            {presetGroups.map((group) => (
                                <div key={group.title} className="color-token-picker__group">
                                    <span className="color-token-picker__group-title">
                                        {translatePresetGroupTitle(group.title, t)}
                                    </span>
                                    <div className="color-token-picker__swatches">
                                        {group.items.map((item) => {
                                            const isSelected = normalizeCandidate(value) === item.value;
                                            return (
                                                <button
                                                    type="button"
                                                    key={item.id}
                                                    className={`color-token-picker__swatch${isSelected ? " color-token-picker__swatch--selected" : ""}`}
                                                    onClick={() => handleSelectColor(item.value)}
                                                >
                                                    <span
                                                        className="color-token-picker__swatch-chip"
                                                        style={{ backgroundColor: item.swatch }}
                                                    />
                                                    <span className="color-token-picker__swatch-label">
                                                        {translatePresetColorLabel(item.label, t)}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {recentColors.length > 0 && (
                        <div className="color-token-picker__section">
                            <h4>{t("colorTokenPicker.recentColors", "Recent colors")}</h4>
                            <div className="color-token-picker__recents">
                                {recentColors.map((recent) => {
                                    const swatch = resolvePreviewColor(recent, presetIndex) || ensureHex(recent) || DEFAULT_CUSTOM_COLOR;
                                    return (
                                        <button
                                            type="button"
                                            key={recent}
                                            className="color-token-picker__recent"
                                            onClick={() => handleSelectColor(recent)}
                                        >
                                            <span
                                                className="color-token-picker__recent-chip"
                                                style={{ backgroundColor: swatch }}
                                            />
                                            <span className="color-token-picker__recent-label">
                                                {resolveDisplayText(recent, presetIndex, emptyColorLabel, t)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="color-token-picker__section">
                        <h4>{t("colorTokenPicker.custom", "Custom")}</h4>
                        <form className="color-token-picker__custom" onSubmit={handleCustomSubmit}>
                            <input
                                type="color"
                                className="color-token-picker__custom-color"
                                value={ensureHex(customInput) || ensureHex(value) || DEFAULT_CUSTOM_COLOR}
                                onChange={(event) => setCustomInput(event.target.value)}
                                aria-label={t("colorTokenPicker.customColor", "Choose custom color")}
                            />
                            <input
                                type="text"
                                className="color-token-picker__custom-input"
                                placeholder={t("colorTokenPicker.customPlaceholder", "berry or #3366ff")}
                                value={customInput}
                                onChange={handleCustomChange}
                            />
                            <button type="submit" className="color-token-picker__apply">
                                {t("colorTokenPicker.apply", "Apply")}
                            </button>
                        </form>
                        {customError && (
                            <p className="color-token-picker__error" role="alert">
                                {customError}
                            </p>
                        )}
                    </div>
                    <div className="color-token-picker__footer">
                        <button
                            type="button"
                            className="color-token-picker__clear"
                            onClick={handleClearSelection}
                        >
                            {t("colorTokenPicker.clear", "Clear color")}
                        </button>
                        {previewColor && (
                            <div className="color-token-picker__current">
                                <span className="color-token-picker__current-chip" style={{ backgroundColor: previewColor }} />
                                <span className="color-token-picker__current-label">
                                    {resolveDisplayText(value, presetIndex, emptyColorLabel, t)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

ColorTokenPicker.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    presetGroups: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
            swatch: PropTypes.string.isRequired,
        })).isRequired,
    })),
    ariaLabelledby: PropTypes.string,
    ariaDescribedby: PropTypes.string,
};

export default ColorTokenPicker;
